import { shuffleWithSeed } from "./shuffleSeeded";
import { mcqOptionEntryMedia, mcqOptionEntryText } from "./mcqOptionIds";

/** MCQ option strings in canonical (stored) order. */
export function getMcqOptionTexts(optionsField: Record<string, unknown> | unknown[] | null | undefined | string): string[] {
  let raw: unknown = null;
  let parsedOptions = optionsField;
  
  if (typeof optionsField === "string") {
    try {
      parsedOptions = JSON.parse(optionsField);
    } catch {
      // ignore
    }
  }

  if (Array.isArray(parsedOptions)) {
    raw = parsedOptions;
  } else if (parsedOptions && typeof parsedOptions === "object") {
    raw = (parsedOptions as { options?: unknown }).options || parsedOptions;
  }
  
  if (!Array.isArray(raw)) return [];
  return raw.map(mcqOptionEntryText);
}

export type McqDisplayPair = {
  text: string;
  origIndex: number;
  imageUrl?: string | null;
  audioUrl?: string | null;
  id?: string;
};

/** Pairs for rendering; shuffle only the display order, keep origIndex for selection identity. */
export function buildMcqDisplayPairs(
  q: { options?: Record<string, unknown> | unknown[] | null | string; type?: string },
  shuffle: boolean,
  shuffleSeed: string,
): McqDisplayPair[] {
  let raw: unknown = null;
  let parsedOptions = q.options;
  
  if (typeof q.options === "string") {
    try {
      parsedOptions = JSON.parse(q.options);
    } catch {
      // ignore
    }
  }

  if (Array.isArray(parsedOptions)) {
    raw = parsedOptions;
  } else if (parsedOptions && typeof parsedOptions === "object") {
    raw = (parsedOptions as { options?: unknown }).options || parsedOptions;
  }
  
  if (!Array.isArray(raw)) return [];
  const pairs: McqDisplayPair[] = raw.map((entry, origIndex) => {
    const { imageUrl, audioUrl } = mcqOptionEntryMedia(entry);
    const id = (entry && typeof entry === "object" && "id" in entry) ? String((entry as any).id) : undefined;
    return {
      text: mcqOptionEntryText(entry),
      origIndex,
      imageUrl,
      audioUrl,
      id,
    };
  });
  if (!shuffle || q.type !== "MCQ" || pairs.length <= 1) return pairs;
  return shuffleWithSeed(pairs, shuffleSeed);
}

/** API expects selected option text for scoring. */
export function normalizeMcqAnswersForApi<Q extends { id: string; type: string; options?: unknown | null }>(
  questions: Q[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...answers };
  for (const q of questions) {
    if (q.type !== "MCQ") continue;
    const pairs = buildMcqDisplayPairs(q, false, "");
    const v = out[q.id];
    if (typeof v === "number" && Number.isInteger(v) && v >= 0 && v < pairs.length) {
      const p = pairs[v];
      out[q.id] = p.text?.trim() || p.imageUrl?.trim() || "";
    }
  }
  return out;
}

/** Turn saved string answers into canonical indices for UI (single selection with duplicate labels). */
export function migrateLoadedMcqToIndex<Q extends { type: string; options?: unknown | null }>(
  q: Q,
  value: unknown,
): unknown {
  if (q.type !== "MCQ") return value;
  
  const pairs = buildMcqDisplayPairs(q, false, "");
  if (typeof value === "number" && Number.isInteger(value) && value >= 0 && value < pairs.length) return value;
  if (typeof value !== "string") return value;
  
  const valTrimmed = value.trim();
  const idx = pairs.findIndex((p) => {
    const match = p.text?.trim() || p.imageUrl?.trim() || "";
    return match === valTrimmed || p.id === valTrimmed;
  });
  return idx >= 0 ? idx : value;
}

export function isQuestionAnswered(q: { type: string; options?: unknown | null }, value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (q.type !== "MCQ") {
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  }
  const opts = getMcqOptionTexts(q.options as Record<string, unknown> | null);
  if (opts.length === 0) return false;
  if (typeof value === "number" && Number.isInteger(value)) return value >= 0 && value < opts.length;
  if (typeof value === "string") return value.trim().length > 0;
  return false;
}
